import { useEffect, useState } from "react";
import api from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { useAuth } from "@/lib/auth";
import { User as UserIcon, Bike, Crown, Star } from "lucide-react";

const SECTIONS = [
  { key: "prezydent", label: "Prezydent", icon: Crown },
  { key: "zarzad", label: "Zarząd", icon: Star },
  { key: "member", label: "Members", icon: null },
  { key: "prospect", label: "Prospects", icon: null, membersOnly: true },
];

function getPosition(m) {
  if (m.position && m.position !== "") return m.position;
  // Backwards-compat: admin without explicit position -> prezydent, others -> member
  return m.role === "admin" ? "prezydent" : "member";
}

export default function Members() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/users").then((r) => Array.isArray(r.data) && setMembers(r.data)).catch(() => {});
  }, []);

  const visibleSections = SECTIONS.filter((s) => !s.membersOnly || user);
  const grouped = Object.fromEntries(SECTIONS.map((s) => [s.key, []]));
  members.forEach((m) => {
    const p = getPosition(m);
    if (grouped[p]) grouped[p].push(m);
    else grouped.member.push(m);
  });

  return (
    <div className="max-w-6xl mx-auto px-5 py-12" data-testid="members-page">
      <SectionHeader title="Członkowie" />
      <p className="text-center mt-4 mb-12" style={{ color: "var(--sr-cream-soft)" }}>
        Bracia i siostry naszego klubu.
      </p>

      <div className="space-y-16">
        {visibleSections.map((section) => {
          const list = grouped[section.key] || [];
          if (list.length === 0) return null;
          const Icon = section.icon;
          return (
            <section key={section.key} data-testid={`section-${section.key}`}>
              <div className="flex items-center gap-4 mb-8">
                <span className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, var(--sr-fire), transparent)" }} />
                <h3 className="font-display text-xl md:text-2xl tracking-[0.25em] flex items-center gap-2" style={{ color: "var(--sr-fire)" }}>
                  {Icon && <Icon size={20} />}
                  {section.label.toUpperCase()}
                </h3>
                <span className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, var(--sr-fire), transparent)" }} />
              </div>

              <div className={section.key === "prezydent" ? "max-w-md mx-auto" : "grid sm:grid-cols-2 lg:grid-cols-3 gap-5"}>
                {list.map((m, i) => (
                  <article
                    key={m.id}
                    className="sr-card p-6 fade-up"
                    style={{ animationDelay: `${i * 60}ms` }}
                    data-testid={`member-${m.username}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--sr-bg-3)", border: "1px solid var(--sr-fire)" }}>
                        {m.avatar_url ? (
                          <img src={m.avatar_url} alt={m.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <UserIcon className="text-[var(--sr-fire)]" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-display text-lg text-[var(--sr-cream)] truncate">{m.name}</h4>
                        {m.function_title && (
                          <p className="text-xs font-nav uppercase tracking-widest mt-0.5" style={{ color: "var(--sr-fire-2)" }}>
                            {m.function_title}
                          </p>
                        )}
                      </div>
                    </div>
                    {m.bio && <p className="mt-4 text-sm" style={{ color: "var(--sr-cream-soft)" }}>{m.bio}</p>}
                    {m.motorcycle && (
                      <p className="mt-3 text-sm flex items-center gap-2" style={{ color: "var(--sr-fire-2)" }}>
                        <Bike size={14} /> {m.motorcycle}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </section>
          );
        })}
        {members.length === 0 && (
          <p className="col-span-full text-center" style={{ color: "var(--sr-muted)" }}>Brak danych.</p>
        )}
      </div>
    </div>
  );
}
